import React, { useState, useEffect } from 'react';
import { TextField, Button, Container, Box, Typography, Divider } from '@mui/material';
import { Formik, Form } from 'formik';
import * as Yup from 'yup';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import { createWhitePaper, updateWhitePaper, getWhitePaperById } from '../services/whitePaperService';
import { useParams, useNavigate } from 'react-router-dom';
import DashboardLayout from './DashboardLayout';
import MediaUploadField from './MediaUploadField';
import { uploadWhitePaperDocument } from '../services/uploadService';

// Quill toolbar configuration
const toolbarOptions = [
  [{ header: [1, 2, 3, 4, 5, 6, false] }],
  ['bold', 'italic', 'underline', 'strike'],
  [{ color: [] }, { background: [] }],
  [{ script: 'sub' }, { script: 'super' }],
  [{ font: [] }],
  [{ size: [] }],
  [{ align: [] }],
  [{ list: 'ordered' }, { list: 'bullet' }],
  [{ indent: '-1' }, { indent: '+1' }],
  ['blockquote', 'code-block'],
  ['link', 'image', 'video'],
  ['clean']
];

const modules = {
  toolbar: toolbarOptions
};

function WhitePaperForm() {
  const [initialValues, setInitialValues] = useState({
    whitePaperId: null,
    title: '',
    titleAr: '',
    content: '',
    contentAr: '',
    metaDescription: '',
    metaDescriptionAr: '',
    metaTitle: '',
    metaTitleAr: '',
    keywords: '',
    category: '',
    categoryAr: '',
    imageUrl: '',
    publishDate: new Date().toISOString().split('T')[0],
    altText: '',
    slug: '',
    downloadUrl: ''
  });
  const { id } = useParams();
  const navigate = useNavigate();

  // When editing, load the white paper data
  useEffect(() => {
    if (id) {
      fetchWhitePaper();
    }
  }, [id]);

  const fetchWhitePaper = async () => {
    try {
      const data = await getWhitePaperById(id);
      setInitialValues({
        whitePaperId: data.whitePaperId,
        title: data.title,
        titleAr: data.titleAr || '',
        content: data.content,
        contentAr: data.contentAr || '',
        metaDescription: data.metaDescription,
        metaDescriptionAr: data.metaDescriptionAr || '',
        metaTitle: data.metaTitle,
        metaTitleAr: data.metaTitleAr || '',
        keywords: data.keywords,
        category: data.category,
        categoryAr: data.categoryAr || '',
        imageUrl: data.imageUrl,
        publishDate: data.publishDate
          ? data.publishDate.split('T')[0]
          : new Date().toISOString().split('T')[0],
        altText: data.altText,
        slug: data.slug,
        downloadUrl: data.downloadUrl
      });
    } catch (error) {
      console.error('Error fetching white paper:', error);
      alert(error.friendlyMessage || error?.response?.data?.message || 'Failed to fetch white paper data.');
    }
  };

  const WhitePaperSchema = Yup.object().shape({
    title: Yup.string().required('Title is required'),
    content: Yup.string().required('Content is required'),
    slug: Yup.string().required('Slug is required'),
    category: Yup.string().required('Category is required'),
    // Arabic fields are optional
    titleAr: Yup.string(),
    contentAr: Yup.string(),
    metaDescriptionAr: Yup.string(),
    metaTitleAr: Yup.string(),
    categoryAr: Yup.string(),
  });

  const handleSubmit = async (values) => {
    try {
      if (id) {
        await updateWhitePaper(id, values);
      } else {
        await createWhitePaper(values);
      }
      navigate('/whitepapers');
    } catch (error) {
      console.error('Error saving white paper:', error);
      alert(error.friendlyMessage || error?.response?.data?.message || 'Error saving white paper.');
    }
  };

  return (
    <DashboardLayout>
      <Container maxWidth="md">
        <Box mt={4}>
          <Typography variant="h4" gutterBottom>
            {id ? 'Edit White Paper' : 'Create White Paper'}
          </Typography>
          
          <Formik
            initialValues={initialValues}
            enableReinitialize
            validationSchema={WhitePaperSchema}
            onSubmit={handleSubmit}
          >
            {({ values, errors, touched, handleChange, setFieldValue }) => (
              <Form>
                {/* English Fields Section */}
                <Typography variant="h6" gutterBottom sx={{ mt: 3, mb: 2, color: 'primary.main' }}>
                  English Content
                </Typography>
                <Divider sx={{ mb: 3 }} />

                <TextField
                  label="Title (English)"
                  name="title"
                  value={values.title}
                  onChange={handleChange}
                  variant="outlined"
                  margin="normal"
                  fullWidth
                  required
                  error={touched.title && Boolean(errors.title)}
                  helperText={touched.title && errors.title}
                />

                <TextField
                  label="Slug"
                  name="slug"
                  value={values.slug}
                  onChange={handleChange}
                  variant="outlined"
                  margin="normal"
                  fullWidth
                  required
                  error={touched.slug && Boolean(errors.slug)}
                  helperText={touched.slug && errors.slug}
                />

                <TextField
                  label="Category (English)"
                  name="category"
                  value={values.category}
                  onChange={handleChange}
                  variant="outlined"
                  margin="normal"
                  fullWidth
                  required
                  error={touched.category && Boolean(errors.category)}
                  helperText={touched.category && errors.category}
                />

                {/* Rich Text Editor for English Content */}
                <Box mt={2} mb={2}>
                  <Typography variant="subtitle1" gutterBottom>
                    Content (English) *
                  </Typography>
                  <ReactQuill
                    theme="snow"
                    value={values.content}
                    onChange={(value) => setFieldValue('content', value)}
                    modules={modules}
                  />
                  {touched.content && errors.content && (
                    <Typography color="error">{errors.content}</Typography>
                  )}
                </Box>

                <TextField
                  label="Meta Title (English)"
                  name="metaTitle"
                  value={values.metaTitle}
                  onChange={handleChange}
                  variant="outlined"
                  margin="normal"
                  fullWidth
                />

                <TextField
                  label="Meta Description (English)"
                  name="metaDescription"
                  value={values.metaDescription}
                  onChange={handleChange}
                  variant="outlined"
                  margin="normal"
                  fullWidth
                  multiline
                  rows={3}
                />

                {/* Arabic Fields Section */}
                <Typography variant="h6" gutterBottom sx={{ mt: 4, mb: 2, color: 'secondary.main' }}>
                  Arabic Content (اختياري)
                </Typography>
                <Divider sx={{ mb: 3 }} />

                <TextField
                  label="Title (Arabic) - العنوان"
                  name="titleAr"
                  value={values.titleAr}
                  onChange={handleChange}
                  variant="outlined"
                  margin="normal"
                  fullWidth
                  inputProps={{ dir: 'rtl' }}
                  error={touched.titleAr && Boolean(errors.titleAr)}
                  helperText={touched.titleAr && errors.titleAr}
                />

                <TextField
                  label="Category (Arabic) - الفئة"
                  name="categoryAr"
                  value={values.categoryAr}
                  onChange={handleChange}
                  variant="outlined"
                  margin="normal"
                  fullWidth
                  inputProps={{ dir: 'rtl' }}
                  error={touched.categoryAr && Boolean(errors.categoryAr)}
                  helperText={touched.categoryAr && errors.categoryAr}
                />

                {/* Rich Text Editor for Arabic Content */}
                <Box mt={2} mb={2}>
                  <Typography variant="subtitle1" gutterBottom>
                    Content (Arabic) - المحتوى
                  </Typography>
                  <ReactQuill
                    theme="snow"
                    value={values.contentAr}
                    onChange={(value) => setFieldValue('contentAr', value)}
                    modules={modules}
                    placeholder="أدخل المحتوى باللغة العربية..."
                  />
                  {touched.contentAr && errors.contentAr && (
                    <Typography color="error">{errors.contentAr}</Typography>
                  )}
                </Box>

                <TextField
                  label="Meta Title (Arabic) - العنوان التعريفي"
                  name="metaTitleAr"
                  value={values.metaTitleAr}
                  onChange={handleChange}
                  variant="outlined"
                  margin="normal"
                  fullWidth
                  inputProps={{ dir: 'rtl' }}
                />

                <TextField
                  label="Meta Description (Arabic) - الوصف التعريفي"
                  name="metaDescriptionAr"
                  value={values.metaDescriptionAr}
                  onChange={handleChange}
                  variant="outlined"
                  margin="normal"
                  fullWidth
                  multiline
                  rows={3}
                  inputProps={{ dir: 'rtl' }}
                />

                {/* File Upload and Additional Fields Section */}
                <Typography variant="h6" gutterBottom sx={{ mt: 4, mb: 2, color: 'text.secondary' }}>
                  File Upload & Additional Information
                </Typography>
                <Divider sx={{ mb: 3 }} />

                <MediaUploadField
                  label="White Paper PDF"
                  value={values.downloadUrl}
                  onChange={(nextValue) => setFieldValue('downloadUrl', nextValue)}
                  kind="document"
                  scope="whitepapers"
                  slug={values.slug}
                  requireSlug
                  uploadHandler={uploadWhitePaperDocument}
                  helperText="Upload the downloadable PDF. The slug is used as the file name."
                />

                <TextField
                  label="Keywords"
                  name="keywords"
                  value={values.keywords}
                  onChange={handleChange}
                  variant="outlined"
                  margin="normal"
                  fullWidth
                />

                <MediaUploadField
                  label="White Paper Cover Image"
                  value={values.imageUrl}
                  onChange={(nextValue) => setFieldValue('imageUrl', nextValue)}
                  kind="image"
                  scope="whitepapers"
                  slug={values.slug}
                  error={errors.imageUrl}
                  touched={touched.imageUrl}
                />

                <TextField
                  label="Publish Date"
                  name="publishDate"
                  type="date"
                  value={values.publishDate}
                  onChange={handleChange}
                  variant="outlined"
                  margin="normal"
                  fullWidth
                  required
                  error={touched.publishDate && Boolean(errors.publishDate)}
                  helperText={touched.publishDate && errors.publishDate}
                  InputLabelProps={{ shrink: true }}
                />

                <TextField
                  label="Alt Text"
                  name="altText"
                  value={values.altText}
                  onChange={handleChange}
                  variant="outlined"
                  margin="normal"
                  fullWidth
                />

                <Box mt={4}>
                  <Button type="submit" variant="contained" color="primary" size="large" fullWidth>
                    {id ? 'Update White Paper' : 'Create White Paper'}
                  </Button>
                </Box>
              </Form>
            )}
          </Formik>
        </Box>
      </Container>
    </DashboardLayout>
  );
}

export default WhitePaperForm;
